import {
  defaultDataIdFromObject,
  InMemoryCache,
  type PossibleTypesMap,
  type TypePolicies,
} from "@apollo/client/cache"
import {
  auditFacts,
  auditLogEvent,
  dataSource,
  employee,
  entityParty,
  firmClient,
  layout,
  objectAcl,
  objectMapping,
  queries,
  reportPackage,
  reportPackageDocument,
  searchResult,
  team,
  teamMember,
  threads,
  transaction,
  transactionParty,
  transactionsWithSummaries,
  vaultFields,
} from "src/shared/initializers/typePolicies"
import { auditCapture } from "src/shared/initializers/typePolicies/auditCapture"
import { department } from "src/shared/initializers/typePolicies/department"

const typePolicies: TypePolicies = {
  AdminAgentPlaybookMetadata: { keyFields: ["userId", "id", "version"] },
  AuditLogReportDisplay: { keyFields: false },
  // One row per entity, with no id of its own: a saved choice updates the read in place.
  LegalEntityOnboarding: { keyFields: ["legalEntityId"] },
  // Column IDs identify positions within a saved statement, not global entities.
  StatementTableColumn: { keyFields: false },
  // A group's id joins its members' party ids, but whether each member is in the directory, its roles
  // and its activity differ between the Vendors and Customers summaries, so each keeps its own groups.
  MergeCandidateGroup: { keyFields: false },
  Query: {
    fields: {
      ...queries,
      auditCaptureSources: { merge: false },
      auditCaptureBundles: { merge: false },
      auditCaptureBundleTransactions: { merge: false },
      auditCaptureTransaction: { merge: false },
    },
  },
  ...objectAcl,
  ...teamMember,
  ...team,
  ...employee,
  ...firmClient,
  ...auditFacts,
  ...auditCapture,
  ...auditLogEvent,
  ...transaction,
  ...transactionParty,
  ...transactionsWithSummaries,
  ...threads,
  ...searchResult,
  ...entityParty,
  ...reportPackage,
  ...reportPackageDocument,
  ...layout,
  ...dataSource,
  ...vaultFields,
  ...objectMapping,
  ...department,
}

// A fragment written on a union type matches a member only when the union is listed here. Without
// the entry the cache writes the member's __typename and drops its fields.
export const possibleTypes: PossibleTypesMap = {
  AssistantBlock: [
    "TextBlock",
    "BarChartBlock",
    "ChartBlock",
    "ComparisonChartBlock",
    "QuestionBlock",
    "QAResponseBlock",
    "TransactionResultsBlock",
    "InvoiceResultsBlock",
    "DocumentResultsBlock",
    "FinancialStatementBlock",
  ],
  MetricTerm: ["MetricCategoriesOperand", "MetricSummaryTypeOperand", "MetricOperation"],
  AuditLogSummaryAssertion: [
    "AuditLogDocumentTypeChanged",
    "AuditLogDocumentUploaded",
    "AuditLogDocumentVersionCreated",
    "AuditLogReconciliationCreatedByUser",
    "AuditLogReconciliationDeletedByUser",
    "AuditLogReconciliationFinalized",
    "AuditLogReconciliationReopened",
    "AuditLogReconciliationAutomated",
    "AuditLogReconciliationCreatedBySystem",
    "AuditLogReconciliationReconfigured",
    "AuditLogReconciliationDeletedBySystem",
    "AuditLogFlagResolved",
    "AuditLogFlagsSurfacedSummary",
    "AuditLogCategoryCreated",
    "AuditLogCategoryEdited",
    "AuditLogJournalEntryImportRequested",
    "AuditLogJournalEntryImportCompleted",
    "AuditLogJournalEntryImportFailed",
    "AuditLogReportPublishedAsDraftSummary",
    "AuditLogReportPublishedAsFinalSummary",
    "AuditLogTransactionCreatedManualSummary",
    "AuditLogTransactionDeletedByUserSummary",
    "AuditLogTransactionCreatedBySystemSummary",
    "AuditLogSyncedTransactionDeletedByUserSummary",
    "AuditLogTransactionEditedBySupersedeSummary",
    "AuditLogTransactionCategoryChangedSummary",
    "AuditLogTransactionDepartmentChangedSummary",
    "AuditLogTransactionLocationChangedSummary",
    "AuditLogTransactionDetailsChangedSummary",
    "AuditLogTransactionPartyChangedSummary",
    "AuditLogTransactionLedgerPartyChanged",
  ],
  AuditLogAssertion: [
    "AuditLogFlagResolved",
    "AuditLogFlagsSurfaced",
    "AuditLogCategoryCreated",
    "AuditLogReconciliationCreatedByUser",
    "AuditLogReconciliationDeletedByUser",
    "AuditLogReconciliationFinalized",
    "AuditLogReconciliationReopened",
    "AuditLogReconciliationAutomated",
    "AuditLogReconciliationCreatedBySystem",
    "AuditLogReconciliationReconfigured",
    "AuditLogReconciliationDeletedBySystem",
    "AuditLogCategoryEdited",
    "AuditLogDocumentTypeChanged",
    "AuditLogDocumentUploaded",
    "AuditLogDocumentVersionCreated",
    "AuditLogReportPublishedAsDraft",
    "AuditLogReportPublishedAsFinal",
    "AuditLogJournalEntryImportRequested",
    "AuditLogJournalEntryImportCompleted",
    "AuditLogJournalEntryImportFailed",
    "AuditLogTransactionCreatedManual",
    "AuditLogTransactionDeletedByUser",
    "AuditLogTransactionCreatedBySystem",
    "AuditLogSyncedTransactionDeletedByUser",
    "AuditLogTransactionEditedBySupersede",
    "AuditLogTransactionCategoryChanged",
    "AuditLogTransactionDepartmentChanged",
    "AuditLogTransactionLocationChanged",
    "AuditLogTransactionDetailsChanged",
    "AuditLogTransactionPartyChanged",
    "AuditLogTransactionLedgerPartyChanged",
  ],
  AuditLogDisplay: [
    "AuditLogTransactionDisplay",
    "AuditLogCategoryDisplay",
    "AuditLogReconciliationDisplay",
    "AuditLogReportDisplay",
    "AuditLogPartyDisplay",
    "AuditLogDepartmentDisplay",
    "AuditLogLocationDisplay",
  ],
}

export const cache = new InMemoryCache({
  typePolicies,
  possibleTypes,
  dataIdFromObject: (object) => {
    switch (object.__typename) {
      case "InvoiceDiscount":
        return `InvoiceDiscount:${object.invoiceDiscountId}`
      case "BankAccount":
        return `BankAccount:${object.id}:${object.isDefault}:${object.isAutoPayout}`
      case "Check":
        return `Check:${object.id}:${object.isDefault}:${object.isAutoPayout}`
      // Turn off caching for specific types
      // Category is included here to address a hierarchy rendering issue.
      case "Category":
      case "CategoryTransactionSummary":
      case "QuickBooksIntegration":
      case "User":
      case "InvoicingProfile":
      case "Customer":
      case "CustomerSummary":
      case "CustomerAddress":
      case "InvoicingAddress":
      case "Discount":
      case "VendorSummary":
      case "ScheduleEditedObject":
      case "ScheduleSnapshot":
        return undefined
      default:
        return defaultDataIdFromObject(object)
    }
  },
})
